import React from 'react';
import {
  ResponsiveContainer,
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
} from 'recharts';
import './analytics.css';

/**
 * AnalyticsLineChart - Time series line chart
 *
 * Props:
 *   data       - Array of objects with date + metric fields
 *   lines      - Array of { key, label, color } defining each line
 *   xKey       - Key for x-axis (default: 'date')
 *   height     - Chart height in px (default: 300)
 *   title      - Chart title (optional)
 *   loading    - Show loading skeleton (optional)
 *   formatX    - Function to format x-axis labels (optional)
 *   formatY    - Function to format y-axis labels (optional)
 */

function formatDateLabel(dateStr) {
  if (!dateStr || dateStr.length !== 8) return dateStr;
  // GA4 returns dates as YYYYMMDD
  const month = parseInt(dateStr.substring(4, 6), 10);
  const day = parseInt(dateStr.substring(6, 8), 10);
  return `${month}/${day}`;
}

export default function AnalyticsLineChart({
  data = [],
  lines = [],
  xKey = 'date',
  height = 300,
  title,
  loading,
  formatX,
  formatY,
}) {
  if (loading) {
    return (
      <div className="an-chart-container">
        {title && <h4 className="an-chart-title">{title}</h4>}
        <div className="an-chart-skeleton" style={{ height }} />
      </div>
    );
  }

  if (!data || data.length === 0) {
    return (
      <div className="an-chart-container">
        {title && <h4 className="an-chart-title">{title}</h4>}
        <div className="an-chart-empty" style={{ height }}>
          <span>No data available yet</span>
        </div>
      </div>
    );
  }

  const xFormatter = formatX || formatDateLabel;

  return (
    <div className="an-chart-container">
      {title && <h4 className="an-chart-title">{title}</h4>}
      <ResponsiveContainer width="100%" height={height}>
        <LineChart data={data} margin={{ top: 5, right: 20, left: 0, bottom: 5 }}>
          <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
          <XAxis
            dataKey={xKey}
            tickFormatter={xFormatter}
            tick={{ fontSize: 12, fill: '#64748b' }}
            axisLine={{ stroke: '#cbd5e1' }}
          />
          <YAxis
            tickFormatter={formatY}
            tick={{ fontSize: 12, fill: '#64748b' }}
            axisLine={{ stroke: '#cbd5e1' }}
            width={45}
          />
          <Tooltip
            contentStyle={{
              background: '#fff',
              border: '1px solid #e2e8f0',
              borderRadius: '8px',
              fontSize: '13px',
            }}
            labelFormatter={xFormatter}
          />
          {lines.length > 1 && (
            <Legend
              wrapperStyle={{ fontSize: '12px', paddingTop: '8px' }}
            />
          )}
          {lines.map((line) => (
            <Line
              key={line.key}
              type="monotone"
              dataKey={line.key}
              name={line.label}
              stroke={line.color}
              strokeWidth={2}
              dot={{ r: 3, fill: line.color }}
              activeDot={{ r: 5 }}
            />
          ))}
        </LineChart>
      </ResponsiveContainer>
    </div>
  );
}
